// @flow
import RootNodeProvider from "../RootNodeProvider";
import {RoleTypes} from "../../models/Role";
import {SHIPPING_TYPES} from "../../models/ShippingTypes";
import type {Product} from "../../models/Product";
import {currentRouterPath as resolveCurrentRouterPath} from "../routerMode";

/**
 * Internal tracking events: CustomEvents dispatched on the module's root node
 * so the embedding page can subscribe to them (see docs/HPM Events documented).
 * They are fired regardless of any analytics consent — no data leaves the
 * browser; acting on them is the responsibility of the embedding page.
 */

const randomHash = (): string => {
    if (window.crypto && window.crypto.getRandomValues) {
        const bytes = window.crypto.getRandomValues(new Uint8Array(8));
        return Array.from(bytes).map(byte => byte.toString(16).padStart(2, "0")).join("");
    }
    return Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2);
};

export const INSTANCE_ID: string = randomHash();

const DELIVERY_METHODS = {
    [SHIPPING_TYPES.PICKUP]: "pickup",
    [SHIPPING_TYPES.MOUNT]: "installation",
    [SHIPPING_TYPES.SHIPPING]: "shipping",
};

export const toDeliveryMethod = (shippingMethod: any): ?string => {
    const code = shippingMethod && typeof shippingMethod === "object" ? shippingMethod.code : shippingMethod;
    return DELIVERY_METHODS[code];
};

export const toProductDetails = (product: Product, options: {amount?: number, shippingMethod?: any} = {}) => {
    const details = {
        product_ref_no: product.id,
        article_no: product.articleNumber,
        title: product.name,
        price: product.price ? product.price.value : null,
        currency: product.price ? product.price.currency : null,
    };
    if (options.amount != null) details.amount = options.amount;
    const deliveryMethod = toDeliveryMethod(options.shippingMethod);
    if (deliveryMethod) details.delivery_method = deliveryMethod;
    return details;
};

// the route as the module router sees it: fragment, `shop` query parameter or
// pathname depending on the router mode (see helper/routerMode)
export const currentRouterPath = (): string => resolveCurrentRouterPath();

// selection_source handover for category selections that travel through a
// route change (overview tile / lane click -> route -> categorySet)
let pendingCategorySource: ?string = null;
export const setCategorySelectionSource = (source: string) => {
    pendingCategorySource = source;
};
export const takeCategorySelectionSource = (): ?string => {
    const source = pendingCategorySource;
    pendingCategorySource = null;
    return source;
};

const buildContext = (state: any) => {
    const config = state.config || {};
    const filter = (state.filter || {}).filter || {};
    const dealership = (config.dealerDetails || {}).dealership || {};
    return {
        instance_id: INSTANCE_ID,
        event_timestamp: new Date().toISOString(),
        module_config_type: config.garageGroupId ? "group" : (config.garageId ? "garage" : "dealer"),
        module_config_no: config.garageGroupId || config.garageId || config.dealerId || null,
        module_config_role: config.role === RoleTypes.DISTRIBUTOR ? "central search" : "normal",
        module_language: config.moduleLanguage || config.dealerLanguage || null,
        selected_brand: dealership.manufacturerName || dealership.manufacturerTitle || null,
        selected_category: filter.selectedCategory || null,
        selected_vehicle: filter.vehicleKey || null,
    };
};

/**
 * Dispatches a CustomEvent on the module root node. `state` is optional —
 * the store is required lazily to avoid an import cycle
 * (store -> middleware -> this module).
 */
export const emitInternalEvent = (name: string, payload: any = null, state: any = null) => {
    try {
        const root = RootNodeProvider.getRootNode();
        if (!root) return;
        // eslint-disable-next-line global-require
        const currentState = state || require("../../store/index").default.getState();
        root.dispatchEvent(new CustomEvent(name, {
            detail: {
                name,
                context: buildContext(currentState),
                payload,
            },
        }));
    } catch (e) {
        console.error(e);
    }
};

// convenience for components that need the currently viewed product
export const currentProductDetails = (): ?{} => {
    try {
        // eslint-disable-next-line global-require
        const state = require("../../store/index").default.getState();
        const product = (state.content || {}).product;
        return (product && product.id) ? toProductDetails(product) : null;
    } catch (e) {
        return null;
    }
};
